import React, { useEffect, useState } from 'react';
import { RefreshCw, X, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { apiClient } from '../../services/api';

export type AdminReissuePlayerCredentialsModalProps = {
  open: boolean;
  onClose: () => void;
  userId: string;
  currentUsername?: string | null;
  onSuccess?: () => void;
};

function extractError(err: unknown): string {
  const anyErr = err as { message?: string; response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || anyErr?.message || 'Failed to reissue credentials';
}

const AdminReissuePlayerCredentialsModal: React.FC<AdminReissuePlayerCredentialsModalProps> = ({
  open,
  onClose,
  userId,
  currentUsername,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ username?: string; tempPassword?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    setResult(null);
    setCopied(false);
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (reason.trim().length < 3) {
      setError('Reason is required (min 3 characters)');
      return;
    }
    if (
      !window.confirm(
        'This will assign a NEW username and temporary password, and sign out all player sessions. Continue?'
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.postAdminUserCredentialsReissue(userId, { reason: reason.trim() });
      const payload = (res as { data?: { username?: string; tempPassword?: string } })?.data;
      setResult({
        username: payload?.username,
        tempPassword: payload?.tempPassword,
      });
      onSuccess?.();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCredentials = async () => {
    if (!result?.username || !result?.tempPassword) return;
    await navigator.clipboard.writeText(`Username: ${result.username}\nPassword: ${result.tempPassword}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 !mt-0 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <Card
        className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-b-none sm:rounded-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="space-y-2 pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 min-w-0 pr-2">
              <RefreshCw className="h-5 w-5 shrink-0" />
              <span className="break-words">Reissue credentials</span>
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {result?.username && result?.tempPassword ? (
            <div className="space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Copy these credentials now — the password is shown only once.
              </p>
              <div className="rounded-md border bg-muted/40 p-3 font-mono text-xs sm:text-sm space-y-1 overflow-x-auto">
                <div className="break-all">Username: {result.username}</div>
                <div className="break-all">Password: {result.tempPassword}</div>
              </div>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={copyCredentials}>
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button type="button" className="w-full sm:w-auto" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Current username: <span className="font-medium break-all">{currentUsername || '—'}</span>. A new
                username and temporary password will be generated and sent if contact channels exist.
              </p>
              <div>
                <label className="text-sm font-medium" htmlFor="admin-reissue-reason">
                  Reason
                </label>
                <Input
                  id="admin-reissue-reason"
                  className="mt-1"
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  placeholder="Why are you reissuing credentials?"
                  disabled={submitting}
                />
              </div>
              {error ? <p className="text-sm text-destructive break-words">{error}</p> : null}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
                <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" className="w-full sm:w-auto" disabled={submitting}>
                  {submitting ? 'Reissuing…' : 'Reissue'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReissuePlayerCredentialsModal;
